import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Grid, Divider,
  CircularProgress, IconButton, Tooltip
} from '@mui/material';
import {
  Business, Inventory2, ShoppingCart, Description, Payment,
  ListAlt, SwapHoriz, EditNote, LocationOn, AccountBalance,
  Home, Refresh, Warning, CheckCircleOutline, PendingActions,
  CreditCard, ReceiptLong, TrendingDown, AccountTree, Numbers,
  Tune, Settings, MoveToInbox, Receipt, RequestQuote, Assignment
} from '@mui/icons-material';
import axios from 'axios';
import { fmt, fmtN, sym } from '../utils/currency';

const API = process.env.REACT_APP_API_URL;

// ── KPI Cue card ─────────────────────────────────────────────────────────────
const Cue = ({ icon: Icon, label, value, sub, color, urgent, path, navigate }) => (
  <Card elevation={2} sx={{
    borderRadius: 2.5, cursor: path ? 'pointer' : 'default', height: '100%',
    borderLeft: `4px solid ${urgent ? '#c62828' : color}`,
    transition: 'transform 0.15s, box-shadow 0.15s',
    '&:hover': path ? { boxShadow: 5, transform: 'translateY(-2px)' } : {}
  }} onClick={() => path && navigate(path)}>
    <CardContent sx={{ py: 2 }}>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Typography variant="h4" fontWeight={800}
            color={urgent ? 'error.main' : color} lineHeight={1}>
            {value}
          </Typography>
          <Typography variant="body2" fontWeight={600} color="text.primary" mt={0.5}>{label}</Typography>
          {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
        </Box>
        <Box sx={{
          width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: `${urgent ? '#c62828' : color}18`
        }}>
          <Icon sx={{ fontSize: 22, color: urgent ? '#c62828' : color }} />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ── Nav Tile ──────────────────────────────────────────────────────────────────
const Tile = ({ icon: Icon, label, desc, color, path, navigate }) => (
  <Card elevation={1} sx={{
    borderRadius: 2, cursor: 'pointer', height: '100%',
    transition: 'transform 0.15s, box-shadow 0.15s',
    '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' }
  }} onClick={() => navigate(path)}>
    <CardContent sx={{ py: 1.5, px: 2 }}>
      <Box display="flex" alignItems="center" gap={1.5}>
        <Box sx={{
          width: 38, height: 38, flexShrink: 0, borderRadius: 1.5,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: `${color}18`
        }}>
          <Icon sx={{ fontSize: 21, color }} />
        </Box>
        <Box minWidth={0}>
          <Typography variant="body2" fontWeight={700} lineHeight={1.2} noWrap>{label}</Typography>
          {desc && <Typography variant="caption" color="text.secondary" lineHeight={1.3}>{desc}</Typography>}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ── Section row ───────────────────────────────────────────────────────────────
const Section = ({ title, color, tiles, navigate: nav }) => (
  <Box mb={3.5}>
    <Box display="flex" alignItems="center" mb={1.5}>
      <Box sx={{ width: 4, height: 20, bgcolor: color, borderRadius: 2, mr: 1.5, flexShrink: 0 }} />
      <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
      <Divider sx={{ flex: 1, ml: 1.5 }} />
    </Box>
    <Grid container spacing={1.5}>
      {tiles.map(t => (
        <Grid item xs={12} sm={6} md={3} key={t.label}>
          <Tile {...t} navigate={nav} />
        </Grid>
      ))}
    </Grid>
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────

const PayablesDashboard = () => {
  const navigate = useNavigate();
  const [kpis,    setKpis]    = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchKpis = useCallback(async () => {
    setLoading(true);
    const [invR, pvR, bankR, supR] = await Promise.allSettled([
      axios.get(`${API}/api/payables/invoices`,  { headers }),
      axios.get(`${API}/api/payables/vouchers`,   { headers }),
      axios.get(`${API}/api/finance/banks`,        { headers }),
      axios.get(`${API}/api/payables/suppliers`,   { headers }),
    ]);
    const val = (r) => r.status === 'fulfilled' ? r.value.data : [];

    const invs   = val(invR);
    const pvs    = val(pvR);
    const banks  = val(bankR);
    const sups   = val(supR);
    const now    = new Date();
    const mStart = new Date(now.getFullYear(), now.getMonth(), 1);

    const openInv     = invs.filter(i => ['Open', 'Partial'].includes(i.status));
    const overdueInv  = openInv.filter(i => i.dueDate && new Date(i.dueDate) < now);
    const totalOwed   = openInv.reduce((s, i) => s + ((i.totalAmount || 0) - (i.paid || 0)), 0);
    const monthPv     = pvs.filter(v => v.status === 'Posted' && new Date(v.voucherDate || v.createdAt) >= mStart);
    const monthPaid   = monthPv.reduce((s, v) => s + (v.amount || 0), 0);
    const totalBankBal = banks.reduce((s, b) => s + (b.balance || 0), 0);

    // Suppliers with an outstanding balance (have open invoices)
    const suppliersWithBalance = new Set(openInv.map(i => i.supplierCode)).size;

    setKpis({
      openInvCount:         openInv.length,
      overdueCount:         overdueInv.length,
      totalOwed,
      monthPvCount:         monthPv.length,
      monthPaid,
      totalBankBal,
      supplierCount:        sups.length,
      suppliersWithBalance,
    });
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchKpis(); }, [fetchKpis]);

  const L = loading ? '…' : null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f6f8', py: 3 }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3 } }}>

        {/* ── Header ── */}
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          mb: 3, p: { xs: 2, sm: 3 }, borderRadius: 3,
          background: 'linear-gradient(135deg, #e65100 0%, #bf360c 100%)',
          color: '#fff', boxShadow: 3, flexWrap: 'wrap', gap: 2
        }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>Payables</Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.3 }}>
              Vendors · Purchase Orders · Invoices · Payments · Inventory
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchKpis}
                sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } }}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip title="Finance Manager">
              <IconButton onClick={() => navigate('/finance')}
                sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } }}>
                <Home />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* ── Activities (KPIs) ── */}
        <Box mb={3.5}>
          <Box display="flex" alignItems="center" mb={1.5}>
            <Typography variant="subtitle1" fontWeight={700}>Activities</Typography>
            <Divider sx={{ flex: 1, ml: 1.5 }} />
            {loading && <CircularProgress size={16} sx={{ ml: 1.5 }} />}
          </Box>
          <Grid container spacing={2}>

            <Grid item xs={12} sm={6} md={2.4}>
              <Cue navigate={navigate} icon={PendingActions} label="Open Purchase Invoices" color="#6a1b9a"
                value={L ?? fmtN(kpis?.openInvCount)}
                sub={loading ? '' : `${sym()} ${fmt(kpis?.totalOwed)} outstanding`}
                path="/payables/invoices" />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Cue navigate={navigate} icon={Warning} label="Overdue Invoices" color="#e65100"
                urgent={!loading && (kpis?.overdueCount || 0) > 0}
                value={L ?? fmtN(kpis?.overdueCount)}
                sub="Past due date, still open"
                path="/payables/invoices" />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Cue navigate={navigate} icon={TrendingDown} label="Total Payables" color="#c62828"
                value={L ?? `${sym()} ${fmt(kpis?.totalOwed)}`}
                sub={loading ? '' : `${fmtN(kpis?.suppliersWithBalance)} vendor(s) with balance`}
                path="/payables/invoices" />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Cue navigate={navigate} icon={CheckCircleOutline} label="Payments This Month" color="#2e7d32"
                value={L ?? fmtN(kpis?.monthPvCount)}
                sub={loading ? '' : `${sym()} ${fmt(kpis?.monthPaid)} paid out`}
                path="/payables/vouchers" />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Cue navigate={navigate} icon={AccountBalance} label="Total Bank Balance" color="#00838f"
                value={L ?? `${sym()} ${fmt(kpis?.totalBankBal)}`}
                sub={loading ? '' : `Across all bank accounts`}
                path="/banks" />
            </Grid>

          </Grid>
        </Box>

        <Box my={3}><Divider /></Box>

        {/* ── Vendors ── */}
        <Section navigate={navigate} title="Vendors" color="#e65100" tiles={[
          { icon: Business,    label: 'Suppliers',       desc: 'Vendor master list & contacts',       color: '#e65100', path: '/suppliers' },
          { icon: ReceiptLong, label: 'Supplier Ledger', desc: 'Entries & outstanding balance',        color: '#bf360c', path: '/suppliers' },
        ]} />

        {/* ── Purchase Documents ── */}
        <Section navigate={navigate} title="Purchase Documents" color="#6a1b9a" tiles={[
          { icon: Assignment,   label: 'Purchase Requisitions', desc: 'Request to buy → generate RFQ quotes', color: '#00695c', path: '/payables/purchase-requisitions' },
          { icon: RequestQuote, label: 'Purchase Quotes',    desc: 'RFQ / vendor quotations',               color: '#00838f', path: '/payables/quotes'       },
          { icon: ShoppingCart, label: 'Purchase Orders',    desc: 'LPOs — release, receive, invoice',      color: '#2e7d32', path: '/payables/orders'       },
          { icon: MoveToInbox,  label: 'Goods Receipts',     desc: 'Posted receipts against orders',        color: '#00838f', path: '/payables/receipts'     },
          { icon: Description,  label: 'Purchase Invoices',  desc: 'Vendor invoices & AP entries',          color: '#6a1b9a', path: '/payables/invoices'     },
          { icon: ListAlt,      label: 'Store Requisitions', desc: 'Internal stock issue requests',         color: '#4527a0', path: '/payables/requisitions' },
        ]} />

        {/* ── Payments & Banking ── */}
        <Section navigate={navigate} title="Payments & Banking" color="#00695c" tiles={[
          { icon: Payment,        label: 'Payment Vouchers',   desc: 'Post payments to vendors',              color: '#00695c', path: '/payables/vouchers'           },
          { icon: AccountBalance, label: 'Bank Accounts',      desc: 'Bank & cash account list',              color: '#00838f', path: '/banks'                        },
          { icon: CreditCard,     label: 'Payment Methods',    desc: 'Cash, Cheque, BANK, M-Pesa, RTGS…',   color: '#0277bd', path: '/payables/payment-methods'     },
        ]} />

        {/* ── Stores & Inventory ── */}
        <Section navigate={navigate} title="Stores & Inventory" color="#2e7d32" tiles={[
          { icon: Inventory2,  label: 'Items & Services',   desc: 'Item master — goods & services',        color: '#1565c0', path: '/payables/items'               },
          { icon: EditNote,    label: 'Item Journals',      desc: 'Stock adjustments & write-offs',        color: '#4e342e', path: '/payables/item-journals'       },
          { icon: SwapHoriz,   label: 'Transfer Orders',    desc: 'Stock transfers between locations',     color: '#00796b', path: '/procurement/transfers'        },
          { icon: LocationOn,  label: 'Store Locations',    desc: 'Warehouses & store rooms',              color: '#00695c', path: '/setups/locations'             },
        ]} />

        {/* ── Payables Setups ── */}
        <Section navigate={navigate} title="Payables Setups" color="#455a64" tiles={[
          { icon: CreditCard,  label: 'Payment Methods',    desc: 'Allowed payment types & GL mapping',    color: '#0277bd', path: '/payables/payment-methods'    },
          { icon: Tune,        label: 'VAT Posting Groups', desc: 'Tax rates & GL accounts',               color: '#e65100', path: '/setups/vat-posting-groups'   },
          { icon: Receipt,     label: 'VAT Return',         desc: 'Output − Input VAT for a period',       color: '#e65100', path: '/payables/vat-return'          },
          { icon: TrendingDown, label: 'Payables Aging',    desc: 'What you owe vendors, by age',          color: '#c62828', path: '/payables/aging'               },
          { icon: Numbers,     label: 'Number Series',      desc: 'Document numbering for PO, PINV, PV',  color: '#455a64', path: '/setups/number-series'         },
          { icon: AccountTree, label: 'Chart of Accounts',  desc: 'GL account structure',                  color: '#37474f', path: '/gl-accounts'                  },
          { icon: Settings,    label: 'All Setups',         desc: 'System-wide configuration',             color: '#546e7a', path: '/setups'                       },
        ]} />

      </Box>
    </Box>
  );
};

export default PayablesDashboard;
